import React, { useRef, useLayoutEffect, useState } from 'react';
import type { MenuDayItemDto, MenuAlwaysAvailable } from '@core/interfaces/api';

// ─── Shared types ─────────────────────────────────────────────────────────────

export const MENU_DESCRIPTION_DEFAULT = 'For other dining choices, please visit our \u00e0 la carte menu where you will find additional entr\u00e9es, \nlighter selections, side dishes, and dessert options.';

export interface MenuTemplateProps {
  communityName: string;
  date?: string;
  showDate?: boolean;
  bugImageUrl?: string | null;
  sections: { title: string; items: MenuDayItemDto[] }[];
  alwaysAvailable?: MenuAlwaysAvailable | null;
  textAlign?: 'left' | 'center' | 'right';
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const PAGE_BORDER = '2px solid #636569';

// Matches the current disclaimer text width in the PDF layout.
// Keep this in sync with future fixed-content width changes.
export const MENU_DESCRIPTION_MAX_WIDTH = '5.5in';

export const CENTERED_MENU_DESCRIPTION_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: MENU_DESCRIPTION_MAX_WIDTH,
  marginLeft: 'auto',
  marginRight: 'auto',
  textAlign: 'center',
  overflowWrap: 'break-word',
  wordBreak: 'normal',
  hyphens: 'auto',
};

export const LEFT_ALIGNED_MENU_DESCRIPTION_STYLE: React.CSSProperties = {
  maxWidth: MENU_DESCRIPTION_MAX_WIDTH,
  marginLeft: 0,
  marginRight: 'auto',
  textAlign: 'left',
  overflowWrap: 'break-word',
  wordBreak: 'normal',
  hyphens: 'auto',
};

export const ALACARTE_COLUMN_FLEX: Record<string, [string, string]> = {
  '1/3-2/3': ['0 0 33.333%', '0 0 66.667%'],
  '1/2-1/2': ['0 0 50%', '0 0 50%'],
  '2/3-1/3': ['0 0 66.667%', '0 0 33.333%'],
};

// Categories that are actual entrées (bold in print)
export const ENTREE_CATS = new Set(['Entree', 'Entr\u00e9e', 'Main']);

// Breakfast category sets
export const BREAKFAST_MAIN_CATS = new Set(['Entree', 'Entr\u00e9e', 'Main', 'Appetizer', 'Soup']);
export const BREAKFAST_BEVERAGE_CATS = new Set(['Beverage']);

// Map API category names → display section titles
export const SECTION_LABEL: Record<string, string> = {
  Entree:    'Main Entr\u00e9es',
  'Entr\u00e9e': 'Main Entr\u00e9es',
  Main:      'Main Entr\u00e9es',
  Side:      'Main Entr\u00e9es',
  Bread:     'Main Entr\u00e9es',
  Vegetable: 'Main Entr\u00e9es',
  Appetizer: 'Appetizers',
  Soup:      'Appetizers',
  Salad:     'Appetizers',
  Dessert:   'Dessert',
  Beverage:  'Beverages',
};

// Preferred display order
export const SECTION_ORDER = ['Appetizers', 'Main Entr\u00e9es', 'Dessert', 'Beverages'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatDisplayDate(dateStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

export function extractAlwaysAvailableItems(alwaysAvailable?: MenuAlwaysAvailable | null): MenuDayItemDto[] {
  if (!alwaysAvailable?.menuData) return [];
  return Object.values(alwaysAvailable.menuData as Record<string, unknown>)
    .filter((v): v is MenuDayItemDto[] => Array.isArray(v))
    .flat();
}

// Names that are DiningRD section headers, not actual menu items
const HEADER_NAMES = new Set([
  'dessert', 'desserts', 'entree', 'entrée', 'entrees', 'entrées',
  'appetizer', 'appetizers', 'soup', 'soups', 'salad', 'salads',
  'beverage', 'beverages', 'side', 'sides', 'bread', 'breads',
  'vegetable', 'vegetables', 'main', 'breakfast', 'lunch', 'dinner',
  'morning snack', 'afternoon snack', 'evening snack',
]);

/**
 * Clean items: remove empty names, asterisk-only rows, separator rows,
 * and header-only rows (items whose name is just a category label).
 */
export function cleanItems(items: MenuDayItemDto[]): MenuDayItemDto[] {
  return items.filter((item) => {
    const name = item.RecipeName?.trim() ?? '';
    if (!name || /^\*+$/.test(name)) return false;
    // Drop DiningRD separator rows like "****** DESSERT ******"
    if (/^\*{2,}/.test(name) && /\*{2,}$/.test(name)) return false;
    const cat = item.MenuCategory?.trim() ?? '';
    if (!cat) return false;
    // Filter out à la carte references — handled by the À La Carte tab
    if (/a\s*la\s*carte/i.test(name)) return false;
    // Filter out header-only rows from DiningRD (e.g. "Dessert", "Entrée")
    if (HEADER_NAMES.has(name.toLowerCase())) return false;
    return true;
  });
}

export function groupByCategory(items: MenuDayItemDto[]): Record<string, MenuDayItemDto[]> {
  return items.reduce<Record<string, MenuDayItemDto[]>>((acc, item) => {
    const cat = item.MenuCategory!.trim();
    (acc[cat] ??= []).push(item);
    return acc;
  }, {});
}

// Within a grouped section, sort primary categories first (e.g. Entree before Side)
const CATEGORY_PRIORITY: Record<string, number> = {
  Entree: 0, 'Entrée': 0, Main: 0,
  Side: 1, Vegetable: 2, Bread: 3,
  Appetizer: 0, Soup: 1, Salad: 2,
  Dessert: 0,
  Beverage: 0,
};

export function groupForMealDisplay(items: MenuDayItemDto[]): { title: string; items: MenuDayItemDto[] }[] {
  const sectionMap = new Map<string, MenuDayItemDto[]>();

  for (const item of items) {
    const cat = item.MenuCategory!.trim();
    const label = SECTION_LABEL[cat] ?? cat;
    const bucket = sectionMap.get(label) ?? [];
    bucket.push(item);
    sectionMap.set(label, bucket);
  }

  return [...sectionMap.entries()]
    .sort(([a], [b]) => {
      const ai = SECTION_ORDER.indexOf(a);
      const bi = SECTION_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi) || a.localeCompare(b);
    })
    .map(([title, sectionItems]) => ({
      title,
      items: sectionItems.sort((a, b) => {
        const pa = CATEGORY_PRIORITY[a.MenuCategory?.trim() ?? ''] ?? 99;
        const pb = CATEGORY_PRIORITY[b.MenuCategory?.trim() ?? ''] ?? 99;
        return pa - pb;
      }),
    }));
}

// ─── Shared components ────────────────────────────────────────────────────────

export function withMenuLogoCacheBust(url: string, timestamp = Date.now()): string {
  if (/[?&]timestamp=/.test(url)) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}timestamp=${timestamp}`;
}

export function MenuLogo({ bugImageUrl }: { bugImageUrl: string }) {
  const imageSrc = withMenuLogoCacheBust(bugImageUrl);

  return (
    <div style={{
      width: '200px',
      height: '100px',
      overflow: 'hidden',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <img
        src={imageSrc}
        alt="Logo"
        style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
      />
    </div>
  );
}

// ─── FitToPage ────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  width: '8.5in',
  height: '11in',
  boxSizing: 'border-box',
  backgroundColor: '#fff',
  fontFamily: 'Georgia, "Times New Roman", serif',
  color: '#000',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Wrapper that ensures all content fits on exactly one page.
 *
 * - `fixedContent` (e.g. logo) is rendered at constant size and never scales.
 * - `children` (text) is measured and scaled down if it would overflow the
 *   remaining vertical space after the fixed content.
 * - A single unbroken border wraps everything when `showBorder` is true.
 */
export function FitToPage({
  fixedContent,
  fixedFooter,
  children,
  showBorder,
}: {
  fixedContent?: React.ReactNode;
  fixedFooter?: React.ReactNode;
  children: React.ReactNode;
  showBorder?: boolean;
}) {
  const scalableContainerRef = useRef<HTMLDivElement>(null);
  const scalableContentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const container = scalableContainerRef.current;
    const content = scalableContentRef.current;
    if (!container || !content) return;

    // Reset to measure natural height
    content.style.transform = 'none';
    content.style.width = '100%';
    content.style.minHeight = '0';

    const availableH = container.clientHeight;
    const contentH = content.scrollHeight;

    if (contentH > availableH) {
      const s = Math.max(0.5, availableH / contentH);
      setScale(s);
      content.style.transform = `scale(${s})`;
      content.style.width = `${100 / s}%`;
    } else {
      setScale(1);
      content.style.transform = 'none';
      content.style.width = '100%';
      // Let children fill remaining space
      content.style.minHeight = `${availableH}px`;
    }
  }, [children, fixedContent, fixedFooter]);

  return (
    <div style={pageStyle}>
      <div style={{
        margin: '0.6in 0.7in',
        border: showBorder ? PAGE_BORDER : undefined,
        padding: showBorder ? '32px 44px' : undefined,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
      }}>
        {/* Fixed content (logo) — never scales */}
        {fixedContent && (
          <div style={{ flexShrink: 0 }}>{fixedContent}</div>
        )}
        {/* Scalable content — shrinks to fit remaining space */}
        <div
          ref={scalableContainerRef}
          style={{ flex: 1, overflow: 'hidden' }}
        >
          <div
            ref={scalableContentRef}
            style={{
              transformOrigin: 'top left',
              transform: scale < 1 ? `scale(${scale})` : undefined,
              width: scale < 1 ? `${100 / scale}%` : '100%',
            }}
          >
            {children}
          </div>
        </div>
        {/* Fixed footer — never scales, pinned to bottom */}
        {fixedFooter && (
          <div style={{ flexShrink: 0 }}>{fixedFooter}</div>
        )}
      </div>
    </div>
  );
}
